import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { phoneNumberFormatter } from "../../slices/phone-number-formatter";
import Select from "react-select";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { getCardTransactionsQuery, getSellersQuery } from "../../queries";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import Flatpickr from "react-flatpickr";
import { getFlatpickrLocale } from "../../utils/flatpickr";
import { useTranslation } from "react-i18next";

const getCardType = (pan) => {
  if (!pan) return "Unknown";
  if (pan.startsWith("8600")) return "Uzcard";
  if (pan.startsWith("5614") || pan.startsWith("9860")) return "Humo";
  if (pan.startsWith("4")) return "Visa";
  if (pan.startsWith("5")) return "Mastercard";
  return "Unknown";
};

const Transactions = () => {
  const [date, setDate] = useState();
  const [page, setPage] = useState(1);
  const [seller, setSeller] = useState();
  const [sellerOptions, setSellersOptions] = useState([]);
  const [status, setStatus] = useState();
  const [modal, setModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { t, i18n } = useTranslation();

  const fpRef = React.useRef(null);

  useEffect(() => {
    if (fpRef.current && fpRef.current.flatpickr && fpRef.current.flatpickr.altInput) {
      fpRef.current.flatpickr.altInput.placeholder = t("Select a date");
    }
  }, [t]);

  const flatpickrLocale = getFlatpickrLocale(i18n.language);

  // Controlled inputs for filters
  const [minPriceInput, setMinPriceInput] = useState("");
  const [maxPriceInput, setMaxPriceInput] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Debounced values for API call
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [search, setSearch] = useState("");

  // Status options
  const statusOptions = [
    { value: "success", label: t("success") },
    { value: "failed", label: t("failed") },
    { value: "reversed", label: t("reversed") },
    { value: "reversedWithError", label: t("reversedWithError") },
  ];

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => setMinPrice(minPriceInput), 500);
    return () => clearTimeout(handler);
  }, [minPriceInput]);

  useEffect(() => {
    const handler = setTimeout(() => setMaxPrice(maxPriceInput), 500);
    return () => clearTimeout(handler);
  }, [maxPriceInput]);

  useEffect(() => {
    const handler = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const { data: payments, isLoading } = useQuery({
    ...getCardTransactionsQuery({
      page,
      fromCreatedAt: date?.[0],
      toCreatedAt: date?.[1],
      fromPaymentAmount: minPrice || undefined,
      toPaymentAmount: maxPrice || undefined,
      pan4Last: search || undefined,
      sellerId: seller?.value,
      status: status?.value,
    }),
  });

  const { data: sellers } = useQuery({
    ...getSellersQuery({
      page: 1,
      size: 1000,
    }),
  });

  const columns = useMemo(
    () => [
      {
        header: t("Seller FIO"),
        cell: ({ row }) => (
          <>
            {`${row.original.seller?.user?.firstName || "-"} ${row.original.seller?.user?.lastName || ""
              }`}{" "}
            <br />
            {phoneNumberFormatter(row.original.seller?.user?.phoneNumber)}
          </>
        ),
        enableColumnFilter: false,
      },
      {
        header: t("Date"),
        cell: ({ row }) =>
          dayjs(row.original.createdAt).format("DD.MM.YYYY, HH:mm"),
      },
      {
        header: t("Amount"),
        cell: ({ row }) =>
          `${(row.original.amount / 100).toLocaleString()} uzs`,
        enableColumnFilter: false,
      },
      {
        header: t("Card Type"),
        cell: ({ row }) => getCardType(row.original.boTrans?.pan),
        enableColumnFilter: false,
      },
      {
        header: t("Status"),
        cell: ({ row }) =>
          row.original.status === "completed" ? (
            <Badge color="success">{t(row.original.status)}</Badge>
          ) : row.original.status === "failed" ? (
            <Badge color="danger">{t(row.original.status)}</Badge>
          ) : (
            <Badge color="secondary">{row.original.status}</Badge>
          ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  const clearFilters = () => {
    setDate(null);
    setMinPriceInput("");
    setMaxPriceInput("");
    setSearchInput("");
    setMinPrice("");
    setMaxPrice("");
    setSearch("");
    setSeller("");
    setStatus("");
  };

  useEffect(() => {
    setPage(1);
  }, [date, minPrice, maxPrice, search, status]);

  useEffect(() => {
    if (sellers) {
      setSellersOptions(
        sellers?.data?.data.map((seller) => ({
          value: seller.id,
          label: `${seller?.user?.firstName} ${seller?.user?.lastName}`,
        }))
      );
    }
  }, [sellers]);

  document.title = "Card Transactions | Bazaar";

  const toggle = () => {
    setModal(!modal);
  };

  const handleRowClick = (row) => {
    setSelectedTransaction(row.original);
    toggle();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="terminalList">
              <CardHeader className="border-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h5 className="mb-0 fw-bold">{t("Card Transactions")}</h5>
              </CardHeader>

              <div className="py-3 px-3 mb-3 border border-dashed border-end-0 border-start-0">
                <Row className="g-3">
                  <Col xs="12" sm="6" md="4" lg="2">
                    <Flatpickr
                      ref={fpRef}
                      name="orderDate"
                      className="form-control"
                      placeholder={t("Select a date")}
                      onChange={(e) => setDate(e)}
                      value={date}
                      options={{
                        enableTime: true,
                        altInput: true,
                        mode: "range",
                        altFormat: "d M, Y, G:i K",
                        dateFormat: "d M, Y, G:i K",
                        locale: flatpickrLocale,
                      }}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Input
                      type="number"
                      placeholder={t("Min Price")}
                      value={minPriceInput}
                      onChange={(e) => setMinPriceInput(e.target.value)}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Input
                      type="number"
                      placeholder={t("Max Price")}
                      value={maxPriceInput}
                      onChange={(e) => setMaxPriceInput(e.target.value)}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Input
                      type="text"
                      placeholder={t("Serial number")}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Select
                      value={seller}
                      options={sellerOptions}
                      onChange={(selectedOption) => setSeller(selectedOption)}
                      placeholder={t("Sellers")}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Select
                      value={status}
                      options={statusOptions}
                      onChange={(selectedOption) => setStatus(selectedOption)}
                      placeholder={t("Status")}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    {(date?.[0] ||
                      minPrice ||
                      maxPrice ||
                      search ||
                      seller ||
                      status) && (
                        <Button
                          color="danger"
                          size="md"
                          onClick={clearFilters}
                          style={{ width: "100%" }}
                          className="d-flex align-items-center justify-content-center gap-1"
                        >
                          {t("Clear Filter")}
                        </Button>
                      )}
                  </Col>
                </Row>
              </div>

              <div className="card-body pt-0">
                {payments?.data?.data.length > 0 && (
                  <TableContainer
                    pagination={{
                      setPage,
                      page,
                      total: payments?.pagination.TotalPages,
                    }}
                    columns={columns}
                    data={payments?.data?.data || []}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted"
                    onRowClick={handleRowClick}
                  />
                )}
                {isLoading && <Loader />}
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{t("Transaction Details")}</ModalHeader>
        <ModalBody>
          {selectedTransaction && (
            <div className="table-responsive">
              <table className="table pull-left">
                <tbody>
                  <tr>
                    <td className="fw-bold">{t("Seller FIO")}</td>
                    <td>
                      {`${selectedTransaction.seller?.user?.firstName || "-"} ${selectedTransaction.seller?.user?.lastName || ""
                        }`}
                      <br />
                      <small className="text-muted">
                        {phoneNumberFormatter(
                          selectedTransaction.seller?.user?.phoneNumber
                        )}
                      </small>
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">{t("Date")}</td>
                    <td>
                      {dayjs(selectedTransaction.createdAt).format(
                        "DD.MM.YYYY, HH:mm"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">{t("Amount")}</td>
                    <td>
                      {(selectedTransaction.amount / 100).toLocaleString()} uzs
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">{t("Card number")}</td>
                    <td>{selectedTransaction.pan4Last}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">{t("Card Type")}</td>
                    <td>{getCardType(selectedTransaction.boTrans?.pan || selectedTransaction.pan4Last)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">{t("Status")}</td>
                    <td>
                      {selectedTransaction.status === "completed" ? (
                        <Badge color="success">
                          {t(selectedTransaction.status)}
                        </Badge>
                      ) : selectedTransaction.status === "failed" ? (
                        <Badge color="danger">
                          {t(selectedTransaction.status)}
                        </Badge>
                      ) : (
                        <Badge color="secondary">
                          {selectedTransaction.status}
                        </Badge>
                      )}
                    </td>
                  </tr>
                  {selectedTransaction.extId && (
                    <tr>
                      <td className="fw-bold">{t("External ID")}</td>
                      <td>{selectedTransaction.extId}</td>
                    </tr>
                  )}
                  {selectedTransaction.id && (
                    <tr>
                      <td className="fw-bold">{t("ID")}</td>
                      <td>{selectedTransaction.id}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Transactions;
